import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client/react';
import {
  type AutocompleteProps,
  type AutocompleteRenderGetTagProps,
  type AutocompleteValue,
  Avatar,
  Button,
  Chip,
  type InputProps,
  ListItem,
  ListItemAvatar,
  ListItemText,
  debounce,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { UserPlus, UserSearch } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type {
  UserSelectFragment,
  Users_Bool_Exp,
  Users_Order_By,
} from '@graphql/__generated__/graphql';
import {
  RaAutoComplete,
  type RaAutoCompleteCreatableItem,
  isCreatableItem,
} from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useRequestVersion } from '@modules/_shared/hooks/useRequestVersion';
import { useLocale } from '@shared/hooks/locale';

import {
  USER_BATCH_SIZE,
  getSelectedUserOptions,
  loadSelectedUsers,
} from './loadSelectedUsers';

export const USER_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment UserSelect on users {
    id
    full_name
    ...UserAvatar
    primaryEmail: emails(
      order_by: [{ primary: desc }, { verified: desc }]
      limit: 1
    ) {
      email
    }
  }
`);

export const SEARCH_USERS = gql(/* GraphQL */ `
  query SearchUsers(
    $q: String!
    $order_by: [users_order_by!]
    $where: users_bool_exp
    $limit: Int = 10
  ) {
    search_users_tsv(
      args: { search_text: $q }
      limit: $limit
      order_by: $order_by
      where: $where
    ) {
      id
      ...UserSelect
    }
  }
`);

const GET_DEFAULT_USERS = gql(/* GraphQL */ `
  query GetDefaultUsers($ids: [uuid!]!) {
    users(where: { id: { _in: $ids } }) {
      id
      ...UserSelect
    }
  }
`);

const LOAD_USERS = gql(/* GraphQL */ `
  query LoadUsers($where: users_bool_exp, $limit: Int) {
    users(where: $where, limit: $limit, order_by: { full_name: asc }) {
      id
      ...UserSelect
    }
  }
`);

export interface UserSelectProps<Multiple extends boolean = false>
  extends Omit<
    AutocompleteProps<UserSelectFragment, Multiple, false, false>,
    'renderInput' | 'options' | 'onChange' | 'value'
  > {
  userId?: AutocompleteValue<string, Multiple, false, false>;
  onChange?: (
    userId: AutocompleteValue<string, Multiple, false, false>,
  ) => void;
  onSelectedUsersChange?: (
    users: UserSelectFragment | UserSelectFragment[] | null,
  ) => void;
  onCreate?: (userData: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  }) => Promise<UserSelectFragment | undefined> | UserSelectFragment | void;
  InputProps?: InputProps;
  orderResultsBy?: Users_Order_By[];
  filters?: Users_Bool_Exp;
  creatable?: boolean;
  limit?: number;
  defaultOptions?: UserSelectFragment[];
  openOnMount?: boolean;
  /**
   * If true, clicking/focusing the field opens the dropdown. It shows
   * `defaultOptions` when provided, otherwise it loads matching users.
   */
  openOnFocus?: boolean;
  /** If true, clears selection after onChange is called (search autocomplete mode) */
  clearOnSelect?: boolean;
  /** Called when the dropdown closes (e.g. to collapse inline pickers). */
  onClose?: AutocompleteProps<
    UserSelectFragment,
    Multiple,
    false,
    false
  >['onClose'];
}

const getCreatableItem = (
  inputStr: string,
): {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
} => {
  // regex don't need to be complex since it's not a validation
  // we just need to know if string is email-like, not if it is valid
  const EMAIL_REGEX = /^\S{1,64}@\S{1,64}\.\S{1,64}$/;

  let first_name: string | null = null;
  let last_name: string | null = null;
  let email: null | string = null;

  if (EMAIL_REGEX.test(inputStr)) {
    email = inputStr;
  } else {
    let lastnames: string[] = [];
    [first_name, ...lastnames] = inputStr.split(' ');

    last_name = lastnames.join(' ');
  }

  return {
    first_name,
    last_name,
    email,
  };
};

export const UserSelect = <Multiple extends boolean = false>({
  onChange,
  onSelectedUsersChange,
  onCreate,
  InputProps,
  userId,
  autoFocus = false,
  disabled,
  orderResultsBy,
  filters,
  sx,
  creatable = false,
  multiple,
  limit = 10,
  defaultOptions,
  openOnMount = false,
  openOnFocus = false,
  clearOnSelect = false,
  onBlur,
  onClose,
}: UserSelectProps<Multiple>) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<UserSelectFragment[]>([]);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const hasActiveSearchRef = useRef(false);
  const defaultOptionsRef = useRef(defaultOptions);
  const [selectedValue, setSelectedValue] = useState<
    AutocompleteValue<
      UserSelectFragment & { isRestricted?: true },
      Multiple,
      false,
      false
    >
  >(
    (multiple ? [] : null) as AutocompleteValue<
      UserSelectFragment,
      Multiple,
      false,
      false
    >,
  );

  const client = useApolloClient();
  const [batchedUsers, setBatchedUsers] = useState<{
    key: string;
    users: UserSelectFragment[];
  }>();
  const [usersLoadFailed, setUsersLoadFailed] = useState(false);
  const [batchRetry, setBatchRetry] = useState(0);
  const selectedIdsKey = JSON.stringify(
    Array.isArray(userId) ? userId : userId == null ? [] : [userId],
  );
  const needsBatches = Array.isArray(userId) && userId.length > USER_BATCH_SIZE;
  const { data: queriedUsers, error: queryError } = useQuery(
    GET_DEFAULT_USERS,
    {
      skip: !userId || needsBatches,
      variables: {
        ids: Array.isArray(userId) ? userId : userId == null ? [] : [userId],
      },
    },
  );
  const hasUsersLoadError = usersLoadFailed || queryError != null;
  const defaultUsers = needsBatches
    ? batchedUsers?.key === selectedIdsKey
      ? batchedUsers
      : undefined
    : queryError == null
    ? queriedUsers
    : undefined;
  const waitingForBatches = needsBatches && defaultUsers == null;
  const knownUsers = defaultUsers?.users ?? batchedUsers?.users;
  const batchedSelectedValue = useMemo(
    () =>
      needsBatches
        ? getSelectedUserOptions<UserSelectFragment & { isRestricted?: true }>(
            JSON.parse(selectedIdsKey) as string[],
            knownUsers,
            id => ({
              id,
              full_name: waitingForBatches
                ? hasUsersLoadError
                  ? t('Error')
                  : t('Loading...')
                : t('Restricted user'),
              ...(waitingForBatches ? {} : { isRestricted: true as const }),
              primaryEmail: [],
              user_images: [],
            }),
          )
        : undefined,
    [
      needsBatches,
      selectedIdsKey,
      knownUsers,
      waitingForBatches,
      hasUsersLoadError,
      t,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    setUsersLoadFailed(false);
    if (!needsBatches) {
      setBatchedUsers(undefined);
      return;
    }
    const ids: string[] = JSON.parse(selectedIdsKey);
    void loadSelectedUsers(ids, async batch => {
      if (cancelled) {
        return [];
      }
      const result = await client.query({
        query: GET_DEFAULT_USERS,
        variables: { ids: batch },
        errorPolicy: 'none',
      });
      if (result.data == null) {
        throw new Error('Failed to load selected users');
      }
      return result.data.users;
    }).then(
      users => {
        if (!cancelled) {
          setBatchedUsers({ key: selectedIdsKey, users });
        }
      },
      error => {
        if (!cancelled) {
          captureException(error);
          setUsersLoadFailed(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [client, selectedIdsKey, needsBatches, batchRetry]);

  useEffect(() => {
    if (defaultUsers != null) {
      if (multiple) {
        setSelectedValue(
          (userId as string[]).map(uId => {
            const user = defaultUsers.users.find(u => u.id === uId);

            return user == null
              ? {
                  id: uId,
                  full_name: t('Restricted user'),
                  isRestricted: true,
                  primaryEmail: [],
                  user_images: [],
                }
              : user;
          }) as AutocompleteValue<
            UserSelectFragment & { isRestricted?: true },
            Multiple,
            false,
            false
          >,
        );
      } else {
        const selectedUser = defaultUsers.users[0];

        setSelectedValue(
          (selectedUser == null && userId != null
            ? {
                id: userId as string,
                full_name: t('Restricted user'),
                isRestricted: true,
                primaryEmail: [],
                user_images: [],
              }
            : selectedUser) as AutocompleteValue<
            UserSelectFragment & { isRestricted?: true },
            Multiple,
            false,
            false
          >,
        );
      }

      if (autoFocus && autocompleteRef.current != null) {
        autocompleteRef.current.querySelector('input')?.focus();
      }
    }
  }, [defaultUsers, selectedIdsKey, autoFocus, multiple, t, userId]);

  // To handle cases where userId is set to null (not handled by previous useEffect because of the skip on useQuery).
  useEffect(() => {
    if (userId == null) {
      setSelectedValue(
        (multiple ? [] : null) as AutocompleteValue<
          UserSelectFragment & { isRestricted?: true },
          Multiple,
          false,
          false
        >,
      );
    }
  }, [userId, multiple]);

  // Apply openOnMount only once on initial mount
  useEffect(() => {
    setOpen(openOnMount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  defaultOptionsRef.current = defaultOptions;

  // Keep default options in sync only while the dropdown is closed so the
  // visible list does not jump under the cursor during selection.
  useEffect(() => {
    if (!open && !hasActiveSearchRef.current) {
      setOptions(defaultOptions ?? []);
    }
  }, [defaultOptions, open]);

  const [searchUsers, { loading: loadingSearch, error }] = useLazyQuery(
    SEARCH_USERS,
    { notifyOnNetworkStatusChange: true },
  );

  const [loadUsers, { loading: loadingInitialUsers }] = useLazyQuery(
    LOAD_USERS,
    { fetchPolicy: 'network-only' },
  );

  const { startRequest, invalidateRequests, isCurrentRequest } =
    useRequestVersion();

  const applyUsers = useCallback(
    (requestId: number, users: UserSelectFragment[]) => {
      if (isCurrentRequest(requestId)) {
        setOptions(users);
        setOpen(true);
      }
    },
    [isCurrentRequest],
  );

  const handleQueryError = useCallback(
    (requestId: number) => {
      if (isCurrentRequest(requestId)) {
        setOpen(false);
        setOptions([]);
      }
    },
    [isCurrentRequest],
  );

  const loadInitialUsers = useCallback(() => {
    const requestId = startRequest();
    loadUsers({
      variables: { where: filters ?? {}, limit },
    })
      .then(response => applyUsers(requestId, response.data?.users ?? []))
      .catch(() => handleQueryError(requestId));
  }, [loadUsers, filters, limit, applyUsers, handleQueryError, startRequest]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const requestId = startRequest();
        if (value.length < 2) {
          hasActiveSearchRef.current = false;
          if (defaultOptionsRef.current != null) {
            setOptions(defaultOptionsRef.current);
          } else if (openOnFocus) {
            loadInitialUsers();
          } else {
            setOptions([]);
          }
          return;
        }

        hasActiveSearchRef.current = true;

        searchUsers({
          variables: {
            q: value,
            order_by: orderResultsBy,
            where: filters,
            limit,
          },
        })
          .then(response =>
            applyUsers(requestId, response.data?.search_users_tsv ?? []),
          )
          .catch(() => handleQueryError(requestId));
      }, 275),
    [
      applyUsers,
      filters,
      handleQueryError,
      limit,
      loadInitialUsers,
      openOnFocus,
      orderResultsBy,
      searchUsers,
      startRequest,
    ],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.clear();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    return () => {
      invalidateRequests();
    };
  }, [invalidateRequests]);

  const setSelectedValueMultiple = useCallback(
    (value: AutocompleteValue<UserSelectFragment, Multiple, false, false>) => {
      setSelectedValue(prevValue => {
        const valueAsArray = Array.isArray(value) ? value : [value];
        const newValue = Array.isArray(prevValue)
          ? [...prevValue, ...valueAsArray]
          : valueAsArray[0];
        onChange?.(
          Array.isArray(newValue) ? newValue.map(user => user.id) : newValue.id,
        );
        onSelectedUsersChange?.(
          newValue as UserSelectFragment | UserSelectFragment[],
        );

        return newValue;
      });
    },
    [setSelectedValue, onChange, onSelectedUsersChange],
  );

  const isRestricted = useMemo(
    () =>
      !multiple &&
      (
        selectedValue as AutocompleteValue<
          UserSelectFragment & { isRestricted?: true },
          false,
          false,
          false
        >
      )?.isRestricted,
    [multiple, selectedValue],
  );

  return (
    <RaAutoComplete<UserSelectFragment, Multiple>
      sx={sx}
      multiple={multiple}
      InputProps={{
        ...InputProps,
        error: hasUsersLoadError || InputProps?.error,
        startAdornment:
          needsBatches && usersLoadFailed ? (
            <Button
              size="small"
              disabled={disabled}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                setUsersLoadFailed(false);
                setBatchRetry(retry => retry + 1);
              }}
            >
              {t('Retry')}
            </Button>
          ) : (
            InputProps?.startAdornment ?? <SelectStartIcon icon={UserSearch} />
          ),
        placeholder: disabled
          ? t('No user')
          : InputProps?.placeholder ?? t('Search users'),
      }}
      renderOption={(
        props: React.HTMLAttributes<HTMLLIElement>,
        option:
          | UserSelectFragment
          | RaAutoCompleteCreatableItem<UserSelectFragment>,
      ) =>
        isCreatableItem(option) ? (
          <ListItem {...props} key="new-user" sx={{ py: 0.75 }}>
            <ListItemAvatar sx={{ minWidth: 40 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                <UserPlus size={16} />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={t('Create {{name}}', { name: option.inputValue })}
              slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
            />
          </ListItem>
        ) : (
          <ListItem {...props} key={option.id} sx={{ py: 0.75 }}>
            <ListItemAvatar sx={{ minWidth: 40 }}>
              <UserAvatar user={option} size={32} />
            </ListItemAvatar>
            <ListItemText
              primary={option.full_name}
              secondary={option.primaryEmail[0]?.email}
              slotProps={{
                primary: { sx: { fontSize: '0.875rem' } },
                secondary: { sx: { fontSize: '0.75rem' } },
              }}
            />
          </ListItem>
        )
      }
      onChange={async (
        _: any,
        value: AutocompleteValue<
          UserSelectFragment | RaAutoCompleteCreatableItem<UserSelectFragment>,
          Multiple,
          false,
          false
        >,
      ) => {
        if (waitingForBatches) {
          return;
        }
        if (Array.isArray(value)) {
          const creatableItem = value.find(isCreatableItem);

          if (creatableItem != null) {
            const result = onCreate?.(
              getCreatableItem(creatableItem.inputValue),
            );

            if (result != null) {
              const newUser = result instanceof Promise ? await result : result;

              if (newUser != null) {
                setSelectedValueMultiple(
                  newUser as AutocompleteValue<
                    UserSelectFragment,
                    Multiple,
                    false,
                    false
                  >,
                );
              }
            }
          } else {
            setSelectedValue(
              value as AutocompleteValue<
                UserSelectFragment,
                Multiple,
                false,
                false
              >,
            );
            onChange?.(
              value.map(user => user.id) as AutocompleteValue<
                string,
                Multiple,
                false,
                false
              >,
            );
            onSelectedUsersChange?.(value as UserSelectFragment[]);
          }
        } else if (value != null && isCreatableItem(value)) {
          const result = onCreate?.(getCreatableItem(value.inputValue));

          if (result != null) {
            const newUser = result instanceof Promise ? await result : result;

            if (newUser != null) {
              setSelectedValue(
                newUser as AutocompleteValue<
                  UserSelectFragment,
                  Multiple,
                  false,
                  false
                >,
              );
              onChange?.(
                newUser.id as AutocompleteValue<string, Multiple, false, false>,
              );
              onSelectedUsersChange?.(newUser);
            }
          }
        } else {
          setSelectedValue(
            value as AutocompleteValue<
              UserSelectFragment,
              Multiple,
              false,
              false
            >,
          );
          onChange?.(
            (value?.id ?? null) as AutocompleteValue<
              string,
              Multiple,
              false,
              false
            >,
          );
          onSelectedUsersChange?.((value as UserSelectFragment | null) ?? null);
        }

        if (clearOnSelect && autocompleteRef.current != null) {
          // Used to trigger the clear on blur event
          autocompleteRef.current.querySelector('input')?.blur();
        }
      }}
      disabled={disabled || isRestricted || waitingForBatches}
      tooltip={
        hasUsersLoadError
          ? t('An error occurred. Please try again.')
          : isRestricted
          ? t("You don't have access to this user")
          : undefined
      }
      error={error != null || hasUsersLoadError}
      value={
        clearOnSelect
          ? ((multiple ? [] : null) as AutocompleteValue<
              UserSelectFragment,
              Multiple,
              false,
              false
            >)
          : ((batchedSelectedValue ?? selectedValue) as AutocompleteValue<
              UserSelectFragment,
              Multiple,
              false,
              false
            >)
      }
      getOptionLabel={option => option.full_name || ''}
      isOptionEqualToValue={(
        option:
          | UserSelectFragment
          | RaAutoCompleteCreatableItem<UserSelectFragment>,
        value:
          | UserSelectFragment
          | RaAutoCompleteCreatableItem<UserSelectFragment>,
      ) => option.id === value.id}
      options={options}
      loading={
        loadingSearch ||
        loadingInitialUsers ||
        (waitingForBatches && !hasUsersLoadError)
      }
      open={open}
      allowEmptySearch={openOnFocus}
      onOpen={
        openOnFocus
          ? () => {
              if (defaultOptionsRef.current != null) {
                setOptions(defaultOptionsRef.current);
                setOpen(true);
              } else {
                loadInitialUsers();
              }
            }
          : undefined
      }
      openOnFocus={openOnFocus}
      renderValue={
        multiple
          ? (((
              value: (
                | (UserSelectFragment & { isRestricted?: true })
                | RaAutoCompleteCreatableItem<UserSelectFragment>
              )[],
              getTagProps: AutocompleteRenderGetTagProps,
            ) =>
              value
                .map((option, index) => {
                  if (isCreatableItem(option)) {
                    return null;
                  }

                  const {
                    key,
                    disabled: chipDisabled,
                    ...tagProps
                  } = getTagProps({ index });

                  return (
                    <Chip
                      sx={{ '& .MuiAvatar-root': { ml: 0.5, mr: -0.5 } }}
                      key={key}
                      size="small"
                      label={option.full_name}
                      avatar={<UserAvatar user={option} size={18} />}
                      {...tagProps}
                      disabled={chipDisabled || option.isRestricted}
                    />
                  );
                })
                .filter(Boolean)) as unknown as never)
          : undefined
      }
      onClose={(event, reason) => {
        hasActiveSearchRef.current = false;
        invalidateRequests();
        setOpen(false);
        setOptions(defaultOptionsRef.current ?? []);
        onClose?.(event, reason);
      }}
      autoFocus={autoFocus}
      onBlur={onBlur}
      debouncedSearch={debouncedSearch}
      setOpen={setOpen}
      setOptions={setOptions}
      ref={autocompleteRef}
      creatable={
        creatable
          ? {
              elementPosition: 'first',
              createItemFn: inputValue => ({ new: true, inputValue }),
            }
          : undefined
      }
    />
  );
};
